import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import i18nInstance from '../i18n';

// Defensive fallback: if REACT_APP_BACKEND_URL is missing (env var stripped at
// build time, mis-configured deployment, etc.), use the current origin so the
// app keeps serving /api routes through the front-door host instead of blowing
// up with a literal "undefined/api/..." baseURL. Same-origin /api is the
// canonical YUMIX deployment shape — this fallback is a no-op when the env
// is correctly set.
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location && window.location.origin) ||
  '';
const API = `${BACKEND_URL}/api`;
if (!process.env.REACT_APP_BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    '[YUMIX] REACT_APP_BACKEND_URL is undefined — falling back to current origin:',
    BACKEND_URL,
  );
}

// Axios instance — Header-based auth (X-Session-Token) ONLY. Zero cookies.
const api = axios.create({ baseURL: API });

// Iter. 406.fr.54 — bind offline queue to this axios instance so failed
// mutations can be persisted + replayed when connectivity returns.
try {
  // eslint-disable-next-line global-require
  const { bindOfflineQueueApi } = require('../utils/offlineQueue');
  bindOfflineQueueApi(api);
} catch (_) { /* utils not present in some test bundles */ }

// Tracks if a refresh is in-flight (prevent multiple simultaneous refreshes)
let _isRefreshing = false;
let _refreshQueue = []; // Requests waiting for refresh to complete

// Request interceptor: attach access token + timezone
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['X-Session-Token'] = token;
  }
  try {
    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    config.headers['X-Timezone'] = 'America/Toronto';
  }
  return config;
});

// Global session-expired callback
let _sessionExpiredCallback = null;

// Response interceptor: auto-refresh on 401 + badge event detection + silent token upgrade
api.interceptors.response.use(
  (response) => {
    // AUTH v100.1 — Silent token refresh from backend response headers
    // Backend emits X-New-Access-Token / X-New-Refresh-Token when it auto-upgrades
    // a legacy session or performs a sliding refresh. We catch them transparently.
    try {
      const newAccess = response?.headers?.['x-new-access-token'];
      const newRefresh = response?.headers?.['x-new-refresh-token'];
      if (newAccess) {
        localStorage.setItem('session_token', newAccess);
      }
      if (newRefresh) {
        localStorage.setItem('refresh_token', newRefresh);
      }
    } catch (e) {
      // Non-blocking
    }

    // Check for new badges in any API response
    const data = response?.data;
    if (data?.new_badges && Array.isArray(data.new_badges) && data.new_badges.length > 0) {
      // Dispatch global event for badge celebrations
      window.dispatchEvent(new CustomEvent('yumix-badge-earned', {
        detail: { badges: data.new_badges }
      }));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';

    // ─────────────────────────────────────────────────────────────────────
    // 🔁 Auto-retry for transient network failures (FETCH_FAILED, NETWORK_ERR,
    // ECONNABORTED, sporadic 502/503/504 from K8s ingress, 520-524 from
    // Cloudflare when origin connection blips).
    // Juin 2026 Yuri spec — quietly retries up to 2 times with backoff
    // (800 ms → 2500 ms) so the user never sees a "fetch failed" toast for
    // a transient infra hiccup.
    // ─────────────────────────────────────────────────────────────────────
    const isNetworkLevelFailure = (
      !error.response &&  // no HTTP response at all
      (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_FAILED' ||
        /Network Error|Failed to fetch|fetch failed/i.test(error.message || '')
      )
    );
    const status = error.response?.status;
    const isGatewayBlip = status && [502, 503, 504].includes(status);
    // Cloudflare 520-524 = origin unreachable from CF edge. The request most
    // likely never reached our backend (or got reset mid-flight), so even
    // mutations are safe to retry transparently — by definition no server-side
    // state change happened.
    const isCloudflareEdgeBlip = status && status >= 520 && status <= 524;
    const isRetryable = isNetworkLevelFailure || isGatewayBlip || isCloudflareEdgeBlip;

    // Only GET/HEAD are idempotent in general. POST/PUT/DELETE may have
    // already mutated state — UNLESS we got a Cloudflare 520-524 (origin
    // never produced a response) or a network-level failure with no response
    // at all. In both cases the backend never sent back 2xx, so retrying is
    // safe (and our critical mutations also carry idempotency_keys as a belt).
    const method = (originalRequest?.method || 'get').toLowerCase();
    const safeMethod = method === 'get' || method === 'head';
    const safeToRetryMutation = isCloudflareEdgeBlip || isNetworkLevelFailure;
    // Auth endpoints handle their own retry semantics — don't double-retry.
    const skipForAuth = url.includes('/auth/');
    // Long-running streaming endpoints (SSE meal analysis) are NOT routed
    // through axios — they use fetch() directly from mealPipeline.js — so we
    // don't need to special-case them here. But guard anyway in case future
    // code paths wrap them.
    const skipForStreaming = /\/meals\/analyze-(stream|text-stream)/.test(url);
    const MAX_NETWORK_RETRIES = 2;
    const allowRetry = (safeMethod || safeToRetryMutation) && !skipForAuth && !skipForStreaming;
    if (originalRequest && isRetryable && allowRetry) {
      originalRequest._netRetryCount = (originalRequest._netRetryCount || 0) + 1;
      if (originalRequest._netRetryCount <= MAX_NETWORK_RETRIES) {
        // Backoff: 800 ms then 2500 ms — short enough to feel instant on a
        // momentary edge hiccup, long enough to let the origin recover.
        const backoff = originalRequest._netRetryCount === 1 ? 800 : 2500;
        // eslint-disable-next-line no-console
        console.warn(
          `[YUMIX] transient ${status || error.code || 'NET'} on ${method.toUpperCase()} ${url} — retry ${originalRequest._netRetryCount}/${MAX_NETWORK_RETRIES} in ${backoff}ms`,
        );
        await new Promise((r) => setTimeout(r, backoff));
        return api(originalRequest);
      }
    }
    // ─────────────────────────────────────────────────────────────────────

    // Skip auth endpoints to avoid loops
    if (url.includes('/auth/session') || url.includes('/auth/refresh') || url.includes('/auth/report-error')) {
      return Promise.reject(error);
    }

    // PAYWALL — backend 402 PAYMENT_REQUIRED → broadcast global event for the modal
    if (error.response?.status === 402) {
      const detail = error.response.data?.detail || {};
      window.dispatchEvent(new CustomEvent('yumix-paywall-blocked', {
        detail: {
          message: detail.message || '',
          reason: detail.error || 'trial_expired',
          access_level: detail.access_level || 'FREE',
          user_type: detail.user_type || 'WORLD',
        },
      }));
    }

    // If 401 and we have a refresh token, try auto-refresh
    if (error.response?.status === 401 && !originalRequest._retried) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        if (_isRefreshing) {
          // Another refresh is in-flight — queue this request
          return new Promise((resolve, reject) => {
            _refreshQueue.push({ resolve, reject, config: originalRequest });
          });
        }

        _isRefreshing = true;
        originalRequest._retried = true;

        try {
          const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = res.data.access_token || res.data.session_token;

          if (newToken) {
            localStorage.setItem('session_token', newToken);

            // Retry original request with new token
            originalRequest.headers['X-Session-Token'] = newToken;

            // Process queued requests
            _refreshQueue.forEach(({ resolve, config }) => {
              config.headers['X-Session-Token'] = newToken;
              resolve(api(config));
            });
            _refreshQueue = [];
            _isRefreshing = false;

            return api(originalRequest);
          }
        } catch (refreshError) {
          _refreshQueue.forEach(({ reject }) => reject(refreshError));
          _refreshQueue = [];
          _isRefreshing = false;

          // Refresh token itself is dead → trigger reconnect
          if (_sessionExpiredCallback) {
            _sessionExpiredCallback();
          }
          return Promise.reject(refreshError);
        }
      }

      // No refresh token available → session expired
      if (_sessionExpiredCallback && localStorage.getItem('session_token')) {
        _sessionExpiredCallback();
      }
    }

    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExchangingSession, setIsExchangingSession] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Register the global 401 callback
  useEffect(() => {
    _sessionExpiredCallback = () => {
      localStorage.removeItem('session_token');
      localStorage.removeItem('refresh_token');
      // Iter. 406.fr.67 — Also wipe cached user_profile on session expiry
      // to prevent cross-user field leak when the next user logs in on the
      // same device (fixes "prénom d'un autre client").
      try { localStorage.removeItem('user_profile'); } catch (_) { /* noop */ }
      setUser(null);
      setSessionExpired(true);
    };
    return () => { _sessionExpiredCallback = null; };
  }, []);

  // Iter. 411.mm21 (OPTION MOYEN) — Skip-if-recent + debounce guard for
  // checkAuth. Prod report: Simon (Pixel 9) sees "ERREUR DE CONNEXION /
  // Network Error" every time he exits a fullscreen video, because the
  // visibilitychange listener (line ~400) fires checkAuth immediately
  // while the Android WebView radio hasn't woken up yet. We refuse to
  // re-run checkAuth if the last successful call landed less than
  // AUTH_CHECK_COOLDOWN_MS ago. This also protects every sibling that
  // listens to visibilitychange (WeeklyQuestCard, BattlePlanTracker)
  // from firing stacked requests on the same wake-up frame.
  const lastAuthCheckRef = useRef(0);
  const AUTH_CHECK_COOLDOWN_MS = 10000; // 10 s per user directive

  const checkAuth = useCallback(async () => {
    if (isExchangingSession) {
      return;
    }

    const hash = window.location.hash || '';
    const search = window.location.search || '';

    if (hash.includes('session_id=') || hash.includes('token=') || search.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }

    // Skip-if-recent: if we successfully touched /auth/me within the last
    // 10 s, do nothing. The user is still authenticated, no round-trip
    // needed. This absorbs the burst from visibilitychange after fullscreen.
    const now = Date.now();
    if (now - lastAuthCheckRef.current < AUTH_CHECK_COOLDOWN_MS) {
      return;
    }

    try {
      const storedToken = localStorage.getItem('session_token');

      if (!storedToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      // The response interceptor handles auto-refresh if this 401s
      const response = await api.get('/auth/me', { timeout: 10000 });
      // Mark this run as the authoritative success — subsequent
      // visibilitychange bursts within 10 s will no-op.
      lastAuthCheckRef.current = Date.now();
      // Merge any locally persisted fields (postal_code, etc.)
      try {
        let persisted = {};
        try { persisted = JSON.parse(localStorage.getItem('user_profile') || '{}'); } catch { persisted = {}; }
        // Iter. 406.fr.67 — Guard against cross-user leak on shared devices.
        // If the persisted profile belongs to a DIFFERENT user, discard it
        // completely instead of merging (fixes "prénom d'un autre client").
        if (persisted && persisted.user_id && response.data?.user_id && persisted.user_id !== response.data.user_id) {
          persisted = {};
        }
        const merged = { ...response.data };
        if (persisted.postal_code && !merged.postal_code) {
          merged.postal_code = persisted.postal_code;
        }
        setUser(merged);
        // Persist the freshly loaded profile so the language hard-lock can read it
        // on cold-boot (Iter. 382). Without this, the user_profile in localStorage
        // could lag one navigation behind the in-memory state.
        try {
          localStorage.setItem('user_profile', JSON.stringify(merged));
        } catch (_) { /* private mode safe */ }
        try {
          if (merged?.language) {
            window.dispatchEvent(
              new CustomEvent('yumix-user-language-changed', {
                detail: { language: merged.language },
              }),
            );
          }
        } catch (_) { /* noop */ }
      } catch {
        setUser(response.data);
        try {
          if (response.data?.language) {
            window.dispatchEvent(
              new CustomEvent('yumix-user-language-changed', {
                detail: { language: response.data.language },
              }),
            );
          }
        } catch (_) { /* noop */ }
      }

      // Auto-resync push subscription to backend after login
      try {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          const swReg = await navigator.serviceWorker.ready;
          const existingSub = await swReg.pushManager.getSubscription();
          if (existingSub) {
            const subData = existingSub.toJSON();
            await api.post('/notifications/subscribe', {
              endpoint: subData.endpoint,
              keys: subData.keys
            });
          }
        }
      } catch (pushErr) {
        // Non-blocking — just log
        console.debug('[Push] Auto-resync skipped:', pushErr.message);
      }
    } catch (error) {
      const hadToken = !!localStorage.getItem('session_token');
      const status = error.response?.status;

      // 🔴 CRITICAL FIX: Only wipe tokens if the server explicitly rejects them (401/403).
      // For timeouts, network errors, 5xx, or offline → KEEP tokens so the user stays
      // logged in on retry. Wiping tokens on transient errors was forcing re-login
      // at every cold start on slow mobile networks (LTE).
      const isAuthRejected = status === 401 || status === 403;

      if (isAuthRejected) {
        setUser(null);
        localStorage.removeItem('session_token');
        localStorage.removeItem('refresh_token');
        // Iter. 406.fr.67 — wipe cached profile too on hard auth rejection
        try { localStorage.removeItem('user_profile'); } catch (_) { /* noop */ }
        if (hadToken) {
          setSessionExpired(true);
        }
      } else {
        // Transient error (timeout, 5xx, offline) — preserve session
        // Try to rehydrate user from last known profile so UI can render.
        try {
          const persisted = JSON.parse(localStorage.getItem('user_profile') || 'null');
          if (persisted && persisted.user_id) {
            setUser(persisted);
          } else {
            setUser(null);
          }
        } catch {
          setUser(null);
        }
        // Do NOT remove tokens — user remains logged in, next request will retry.
        console.warn('[Auth] Transient error on /auth/me, keeping tokens. Status:', status || 'network');
      }
    } finally {
      setLoading(false);
    }
  }, [isExchangingSession]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-check auth when app returns to foreground (important for iOS camera flow)
  // Iter. 411.mm21 (OPTION MOYEN) — debounced 300 ms so a burst of
  // visibilitychange events during the Android fullscreen-exit animation
  // collapse into ONE checkAuth call. Combined with the 10 s skip-if-recent
  // guard inside checkAuth, this makes the exit-from-video path effectively
  // network-less when the token is still fresh.
  useEffect(() => {
    let debounceTimer = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isExchangingSession) {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          debounceTimer = null;
          checkAuth();
        }, 300);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [checkAuth, isExchangingSession]);

  const login = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('session_token');
      localStorage.removeItem('refresh_token');
      // 🔓 Clear the language hard-lock so a new user signing in on this device
      // gets a fresh navigator.language detection (Iter. 391). Without this,
      // a second user inheriting the same browser would see the previous user's
      // saved language.
      try {
        localStorage.removeItem('user_profile');
        localStorage.removeItem('i18nextLng');
        localStorage.removeItem('language');
      } catch (_) { /* private mode */ }
      // Wipe SWR cache so the next user does not see stale data from the previous session
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith('yumix_swr_')) localStorage.removeItem(k);
        }
      } catch { /* noop */ }
      window.location.href = '/';
    }
  };

  const exchangeSession = async (sessionId) => {
    setIsExchangingSession(true);
    try {
      // 🌐 Auto-detect language at first Google OAuth sign-in (Iter. 383). The
      // backend stores it ONLY when creating a new user — existing users keep
      // their persisted choice (Settings dropdown wins forever).
      const detectLang = () => {
        try {
          const raw = (i18nInstance.language || navigator.language || 'fr').slice(0, 2).toLowerCase();
          return ['fr', 'en', 'es'].includes(raw) ? raw : 'fr';
        } catch {
          return 'fr';
        }
      };
      const response = await api.post('/auth/session', {
        session_id: sessionId,
        language: detectLang(),
      });
      const data = response.data;

      // Store access token
      const accessToken = data.access_token || data.session_token;
      if (accessToken) {
        localStorage.setItem('session_token', accessToken);
      } else {
        throw new Error('No access token received from server');
      }

      // Store refresh token (for silent renewal)
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      setUser(data.user);
      setSessionExpired(false);
      setLoading(false);

      return data.user;
    } catch (error) {
      console.error('[Auth] Session exchange error:', error);
      if (error.response?.status === 401 || error.response?.status === 400) {
        throw new Error('Session expirée ou invalide. Veuillez réessayer.');
      } else if (error.response?.status === 500) {
        throw new Error('Erreur serveur. Veuillez réessayer dans quelques instants.');
      } else if (!navigator.onLine) {
        throw new Error('Pas de connexion internet. Vérifiez votre réseau.');
      } else {
        throw new Error(error.response?.data?.detail || error.message || 'Erreur de connexion');
      }
    } finally {
      setTimeout(() => setIsExchangingSession(false), 500);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    // Persist critical fields to localStorage for session continuity.
    // Iter. 406.fr.67 — user_profile is scoped to the CURRENT user_id.
    // Previously we merged blindly with whatever `user_profile` was in
    // localStorage → after logout+login on the same device, fields from
    // the previous user (name, postal_code, ToS-flag…) leaked into the
    // new user's session ("le prénom d'un autre client" bug).
    try {
      const raw = localStorage.getItem('user_profile');
      let existing = {};
      try { existing = raw ? JSON.parse(raw) : {}; } catch { existing = {}; }
      // Discard the old profile if it belongs to a different user.
      if (existing && userData && existing.user_id && userData.user_id && existing.user_id !== userData.user_id) {
        existing = {};
      }
      localStorage.setItem('user_profile', JSON.stringify({ ...existing, ...userData }));
    } catch (_) { /* private mode safe */ }
    // Notify LanguageProvider so the UI re-locks to the user's preferred language
    // (Iter. 382 — prevents Frankenstein UI when user.language differs from detector).
    try {
      if (userData?.language) {
        window.dispatchEvent(
          new CustomEvent('yumix-user-language-changed', {
            detail: { language: userData.language },
          }),
        );
      }
    } catch (_) { /* noop */ }
  };

  // Dynamic Branding — apply theme when user's mode changes
  useEffect(() => {
    if (!user) return;
    const { applyTheme } = require('../utils/themeManager');
    const culturalMode = user.cultural_mode || 'standard';
    const clinicalProfile = user.clinical_mode?.enabled
      ? (user.clinical_mode.condition_types || [])[0] || null
      : null;
    const activeProfile = user.active_profile || null;
    applyTheme(culturalMode, clinicalProfile, activeProfile);
  }, [user?.cultural_mode, user?.clinical_mode, user?.active_profile]);

  // Dynamic Timezone Detection - detect when user travels to a new timezone
  useEffect(() => {
    if (!user) return;
    
    const detectTimezoneChange = async () => {
      try {
        const currentTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const storedTz = user.timezone || localStorage.getItem('last_timezone');
        
        if (storedTz && currentTz !== storedTz) {
          
          // Auto-update on the backend
          await api.put('/users/timezone', { timezone: currentTz });
          
          // Update local state
          setUser(prev => prev ? { ...prev, timezone: currentTz } : prev);
          localStorage.setItem('last_timezone', currentTz);
          
          // Import toast dynamically to avoid circular deps
          const { toast } = await import('sonner');
          toast.info(
            `Fuseau horaire mis à jour: ${currentTz.replace(/_/g, ' ')}`, 
            { duration: 5000 }
          );
        } else if (!storedTz) {
          // First time - store current timezone silently
          localStorage.setItem('last_timezone', currentTz);
          await api.put('/users/timezone', { timezone: currentTz }).catch(() => {});
        }
      } catch (e) {
        console.warn('[Timezone] Detection error:', e);
      }
    };

    detectTimezoneChange();
    
    // Re-check on visibility change (user returns from background after travel)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') detectTimezoneChange();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reconnect = useCallback(() => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setSessionExpired(false);
    login();
  }, [login]);

  const dismissSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const isAuthenticated = !!user && !loading;

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, logout, exchangeSession, updateUser, checkAuth, sessionExpired, reconnect, dismissSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

// Language Context
//
// Migration strategy (Iteration 364 — full i18n refactor):
//   - i18next is the new source of truth (see `/app/frontend/src/i18n/`).
//   - This LanguageContext is kept as a THIN ADAPTER over i18next so the
//     ~180 legacy files using `useLanguage()` + inline `language === 'fr' ? …`
//     keep working without modification.
//   - `changeLanguage()` writes BOTH `i18nextLng` (new) and `language` (legacy
//     localStorage key) so a partial rollout doesn't desync.
//   - `t(key)` first asks i18next; if the key is unknown, falls back to the
//     legacy `translations` dict; if still unknown, returns the key verbatim.
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    // Navigation
    'nav.dashboard': 'Tableau de bord',
    'nav.meals': 'Mes repas',
    'nav.settings': 'Paramètres',
    'nav.clients': 'Clients',
    'nav.logout': 'Déconnexion',
    
    // Landing
    'landing.title': 'EXTRA MILES',
    'landing.subtitle': 'Ton coach nutrition personnel',
    'landing.cta': 'Commencer',
    'landing.feature1.title': 'Analyse Pro',
    'landing.feature1.desc': 'Photographie ton repas et obtiens une analyse nutritionnelle instantanée',
    'landing.feature2.title': 'Quota Protéines',
    'landing.feature2.desc': 'Vérifie si tu atteins ta cible de 0.8-1.5g par livre de poids cible',
    'landing.feature3.title': 'Chef\'s Touch',
    'landing.feature3.desc': 'Suggestions personnalisées pour optimiser chaque repas',
    
    // Dashboard
    'dashboard.welcome': 'Salut',
    'dashboard.today': 'Aujourd\'hui',
    'dashboard.protein': 'Protéines',
    'dashboard.calories': 'Calories',
    'dashboard.meals': 'Repas',
    'dashboard.consistency': 'Score de constance',
    'dashboard.addMeal': 'Ajouter un repas',
    'dashboard.weekly': 'Aperçu hebdomadaire',
    
    // Meal Analysis
    'meal.upload': 'Photographier mon repas',
    'meal.analyzing': 'Analyse en cours…',
    'meal.result': 'Résultat de l\'analyse',
    'meal.foods': 'Aliments détectés',
    'meal.suggestion': 'Suggestion du Chef',
    'meal.alert': 'Alerte',
    
    // Onboarding
    'onboarding.title': 'Ton profil nutrition',
    'onboarding.subtitle': 'Configure tes objectifs pour des recommandations personnalisées',
    'onboarding.weight': 'Poids actuel (lbs)',
    'onboarding.targetWeight': 'Poids cible (lbs)',
    'onboarding.height': 'Taille (cm)',
    'onboarding.age': 'Âge',
    'onboarding.sex': 'Sexe',
    'onboarding.male': 'Homme',
    'onboarding.female': 'Femme',
    'onboarding.activity': 'Niveau d\'activité',
    'onboarding.sedentary': 'Sédentaire',
    'onboarding.light': 'Léger',
    'onboarding.moderate': 'Modéré',
    'onboarding.active': 'Actif',
    'onboarding.veryActive': 'Très actif',
    'onboarding.submit': 'Commencer',
    
    // Settings
    'settings.title': 'Paramètres',
    'settings.profile': 'Profil',
    'settings.language': 'Langue',
    'settings.save': 'Sauvegarder',
    
    // Coach
    'coach.title': 'Tableau de bord Coach',
    'coach.clients': 'Mes clients',
    'coach.alerts': 'Alertes',
    'coach.noAlerts': 'Aucune alerte',
    
    // Common
    'common.loading': 'Chargement…',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.back': 'Retour',
    'common.onTrack': 'Sur la bonne voie',
    'common.low': 'En dessous',
    'common.high': 'Au-dessus',
  },
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.meals': 'My Meals',
    'nav.settings': 'Settings',
    'nav.clients': 'Clients',
    'nav.logout': 'Logout',
    
    // Landing
    'landing.title': 'EXTRA MILES',
    'landing.subtitle': 'Your personal nutrition coach',
    'landing.cta': 'Get Started',
    'landing.feature1.title': 'Pro Analysis',
    'landing.feature1.desc': 'Take a photo of your meal and get instant nutritional analysis',
    'landing.feature2.title': 'Protein Quota',
    'landing.feature2.desc': 'Check if you\'re hitting your 0.8-1.5g per pound target weight goal',
    'landing.feature3.title': 'Chef\'s Touch',
    'landing.feature3.desc': 'Personalized suggestions to optimize every meal',
    
    // Dashboard
    'dashboard.welcome': 'Hey',
    'dashboard.today': 'Today',
    'dashboard.protein': 'Protein',
    'dashboard.calories': 'Calories',
    'dashboard.meals': 'Meals',
    'dashboard.consistency': 'Consistency Score',
    'dashboard.addMeal': 'Add Meal',
    'dashboard.weekly': 'Weekly Overview',
    
    // Meal Analysis
    'meal.upload': 'Photograph my meal',
    'meal.analyzing': 'Analyzing...',
    'meal.result': 'Analysis Result',
    'meal.foods': 'Foods Detected',
    'meal.suggestion': 'Chef\'s Suggestion',
    'meal.alert': 'Alert',
    
    // Onboarding
    'onboarding.title': 'Your nutrition profile',
    'onboarding.subtitle': 'Set up your goals for personalized recommendations',
    'onboarding.weight': 'Current Weight (lbs)',
    'onboarding.targetWeight': 'Target Weight (lbs)',
    'onboarding.height': 'Height (cm)',
    'onboarding.age': 'Age',
    'onboarding.sex': 'Sex',
    'onboarding.male': 'Male',
    'onboarding.female': 'Female',
    'onboarding.activity': 'Activity Level',
    'onboarding.sedentary': 'Sedentary',
    'onboarding.light': 'Light',
    'onboarding.moderate': 'Moderate',
    'onboarding.active': 'Active',
    'onboarding.veryActive': 'Very Active',
    'onboarding.submit': 'Get Started',
    
    // Settings
    'settings.title': 'Settings',
    'settings.profile': 'Profile',
    'settings.language': 'Language',
    'settings.save': 'Save',
    
    // Coach
    'coach.title': 'Coach Dashboard',
    'coach.clients': 'My Clients',
    'coach.alerts': 'Alerts',
    'coach.noAlerts': 'No alerts',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.back': 'Back',
    'common.onTrack': 'On Track',
    'common.low': 'Below Target',
    'common.high': 'Above Target',
  },
  es: {
    // Navigation
    'nav.dashboard': 'Panel',
    'nav.meals': 'Mis comidas',
    'nav.settings': 'Ajustes',
    'nav.clients': 'Clientes',
    'nav.logout': 'Cerrar sesión',

    // Landing
    'landing.title': 'EXTRA MILES',
    'landing.subtitle': 'Tu coach de nutrición personal',
    'landing.cta': 'Empezar',
    'landing.feature1.title': 'Análisis Pro',
    'landing.feature1.desc': 'Fotografía tu comida y obtén un análisis nutricional al instante',
    'landing.feature2.title': 'Cuota de proteínas',
    'landing.feature2.desc': 'Verifica si alcanzas tu objetivo de 0.8–1.5g por libra',
    'landing.feature3.title': "Toque del Chef",
    'landing.feature3.desc': 'Sugerencias personalizadas para optimizar cada comida',

    // Dashboard
    'dashboard.welcome': 'Hola',
    'dashboard.today': 'Hoy',
    'dashboard.protein': 'Proteínas',
    'dashboard.calories': 'Calorías',
    'dashboard.meals': 'Comidas',
    'dashboard.consistency': 'Puntaje de constancia',
    'dashboard.addMeal': 'Agregar comida',
    'dashboard.weekly': 'Resumen semanal',

    // Meal Analysis
    'meal.upload': 'Fotografiar mi comida',
    'meal.analyzing': 'Analizando…',
    'meal.result': 'Resultado del análisis',
    'meal.foods': 'Alimentos detectados',
    'meal.suggestion': 'Sugerencia del Chef',
    'meal.alert': 'Alerta',

    // Onboarding
    'onboarding.title': 'Tu perfil de nutrición',
    'onboarding.subtitle': 'Configura tus objetivos para recibir recomendaciones personalizadas',
    'onboarding.weight': 'Peso actual (lbs)',
    'onboarding.targetWeight': 'Peso objetivo (lbs)',
    'onboarding.height': 'Altura (cm)',
    'onboarding.age': 'Edad',
    'onboarding.sex': 'Sexo',
    'onboarding.male': 'Hombre',
    'onboarding.female': 'Mujer',
    'onboarding.activity': 'Nivel de actividad',
    'onboarding.sedentary': 'Sedentario',
    'onboarding.light': 'Ligero',
    'onboarding.moderate': 'Moderado',
    'onboarding.active': 'Activo',
    'onboarding.veryActive': 'Muy activo',
    'onboarding.submit': 'Empezar',

    // Settings
    'settings.title': 'Ajustes',
    'settings.profile': 'Perfil',
    'settings.language': 'Idioma',
    'settings.save': 'Guardar',

    // Coach
    'coach.title': 'Panel del Coach',
    'coach.clients': 'Mis clientes',
    'coach.alerts': 'Alertas',
    'coach.noAlerts': 'Sin alertas',

    // Common
    'common.loading': 'Cargando…',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.back': 'Atrás',
    'common.onTrack': 'En camino',
    'common.low': 'Por debajo',
    'common.high': 'Por encima',
  }
};

export const LanguageProvider = ({ children }) => {
  // Initial state comes from i18next (which already resolved localStorage /
  // navigator / fallback). We keep a React state mirror so re-renders happen
  // when the user changes language at runtime.
  const [language, setLanguage] = useState(() => {
    const lng = (i18nInstance.language || 'fr').slice(0, 2);
    return ['fr', 'en', 'es'].includes(lng) ? lng : 'fr';
  });

  // `?debug_i18n=1` query param wraps every t() output with the resolved key
  // so the user / QA / translators can spot the EXACT i18n key behind every
  // visible string. The wrapping format is `⟦key⟧ value` — visually distinct
  // and copy-pastable for bug reports. Activated at mount; reload to disable.
  const debugI18n = (() => {
    try {
      if (typeof window === 'undefined') return false;
      const params = new URLSearchParams(window.location.search);
      return params.get('debug_i18n') === '1';
    } catch (_) { return false; }
  })();

  useEffect(() => {
    // Subscribe to i18next so external changes (Settings save, dev tools)
    // propagate to all `useLanguage()` consumers.
    const handler = (lng) => {
      const short = (lng || 'fr').slice(0, 2);
      if (['fr', 'en', 'es'].includes(short)) setLanguage(short);
    };
    i18nInstance.on('languageChanged', handler);
    return () => { i18nInstance.off('languageChanged', handler); };
  }, []);

  // 🔒 LANGUAGE HARD-LOCK (Iter. 382)
  // Whenever the authenticated user's `language` field is known (DB source of truth),
  // it OVERRIDES the navigator/localStorage detection so the UI never displays in a
  // language the user did NOT explicitly choose. Prevents the "Frankenstein UI" bug
  // where Select says "Français" but body renders in Spanish + nav in English because
  // the LanguageDetector and the user profile disagreed.
  useEffect(() => {
    try {
      const persisted = JSON.parse(localStorage.getItem('user_profile') || 'null');
      const userLang = persisted?.language;
      if (!userLang) return;
      const short = String(userLang).slice(0, 2).toLowerCase();
      if (!['fr', 'en', 'es'].includes(short)) return;
      if (i18nInstance.language && i18nInstance.language.slice(0, 2) === short) return;
      i18nInstance.changeLanguage(short);
      try {
        localStorage.setItem('language', short);
        localStorage.setItem('i18nextLng', short);
      } catch (_) { /* private mode safe */ }
    } catch (_) { /* malformed user_profile — ignore */ }
    // Re-run whenever user_profile is rewritten (login, profile save, etc.).
    // We poll storage via a custom event since localStorage 'storage' only
    // fires cross-tab. AppContext's updateUser will dispatch our event.
  }, []);

  // Listen for user_profile updates pushed by AuthProvider.updateUser so the
  // hard-lock above re-runs without a full page reload after a profile save.
  useEffect(() => {
    const onUserUpdate = (e) => {
      const lang = e?.detail?.language;
      if (!lang) return;
      const short = String(lang).slice(0, 2).toLowerCase();
      if (!['fr', 'en', 'es'].includes(short)) return;
      if (i18nInstance.language && i18nInstance.language.slice(0, 2) === short) return;
      i18nInstance.changeLanguage(short);
      try {
        localStorage.setItem('language', short);
        localStorage.setItem('i18nextLng', short);
      } catch (_) { /* noop */ }
    };
    window.addEventListener('yumix-user-language-changed', onUserUpdate);
    return () => window.removeEventListener('yumix-user-language-changed', onUserUpdate);
  }, []);

  const changeLanguage = (lang) => {
    if (!['fr', 'en', 'es'].includes(lang)) return;
    i18nInstance.changeLanguage(lang);
    setLanguage(lang);
    try {
      // Write BOTH keys for backward-compat with any legacy reader.
      localStorage.setItem('language', lang);
      localStorage.setItem('i18nextLng', lang);

      // 🔒 Iter. 406.fr.21 — also patch the cached user_profile so the
      // hard-lock effect (line 856) doesn't revert to the old DB value on
      // the next mount/refresh. The "Frankenstein UI" recurrence root cause.
      try {
        const raw = localStorage.getItem('user_profile');
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.language = lang;
          localStorage.setItem('user_profile', JSON.stringify(parsed));
        }
      } catch (_) { /* malformed user_profile — ignore */ }
    } catch (_) { /* SSR / no-storage envs */ }

    // 🔒 Persist to backend so the DB (single source of truth) is in sync.
    // Fire-and-forget — we don't block the UI on the network round-trip.
    try {
      api.put('/users/profile', { language: lang }).catch(() => {
        // Network errors are non-fatal: the local change is already applied
        // and the next successful profile save will reconcile.
      });
    } catch (_) { /* `api` may be undefined in SSR / tests */ }

    // Broadcast so the user_profile hard-lock listener in this same
    // AppContext doesn't fight the change after the next render.
    try {
      window.dispatchEvent(new CustomEvent('yumix-user-language-changed', {
        detail: { language: lang },
      }));
    } catch (_) { /* CustomEvent unsupported — ignore */ }
  };

  // `t()` API:
  //   1. signatures supported:
  //        t('key')
  //        t('key', { interpolation })       — react-i18next idiomatic
  //        t('key', 'default string')        — i18next-parser-generated short form
  //        t('key', 'default string', { interpolation })  — i18next-parser default + vars
  //   2. ask i18next (new translations under namespace "translation")
  //   3. fall back to the legacy `translations` dict
  //   4. last resort: return the (interpolated) default or the key verbatim
  //   + when ?debug_i18n=1 → prepend ⟦key⟧ marker so QA can identify keys
  const t = (key, arg2, arg3) => {
    if (typeof key !== 'string') return key;

    // Normalise the (defaultValue, options) tuple from the variadic call sites.
    let defaultValue = '';
    let opts = {};
    if (typeof arg2 === 'string') {
      defaultValue = arg2;
      if (arg3 && typeof arg3 === 'object') opts = arg3;
    } else if (arg2 && typeof arg2 === 'object') {
      opts = arg2;
      if (typeof opts.defaultValue === 'string') defaultValue = opts.defaultValue;
    }

    const i18nOpts = { defaultValue: '', ...opts };
    const fromI18n = i18nInstance.t(key, i18nOpts);
    // i18next may return the *key itself* (a truthy string) when the key is
    // missing AND defaultValue is empty/falsy. Treat that as "not found" so
    // we fall through to the legacy dict / inline default below instead of
    // leaking the raw key (e.g. "settings_page.cultural_halal_desc") to the
    // UI — those long unbreakable identifiers also trigger horizontal
    // overflow on mobile viewports.
    let resolved = (fromI18n && fromI18n !== key) ? fromI18n : '';

    if (!resolved) {
      const active = translations[language] || translations.en;
      resolved = active[key]
        || (language === 'es' ? translations.en[key] : '')
        || translations.fr[key]
        || '';
      // If we landed on a legacy default (no interpolation), still try to
      // interpolate it with the provided options so {{vars}} get replaced.
      if (resolved && Object.keys(opts).length > 0) {
        resolved = resolved.replace(/\{\{(\w+)\}\}/g, (_, name) =>
          name in opts ? String(opts[name]) : `{{${name}}}`,
        );
      }
      if (!resolved) {
        // Last resort: use the inline default, interpolated locally.
        resolved = (defaultValue || key).replace(/\{\{(\w+)\}\}/g, (_, name) =>
          name in opts ? String(opts[name]) : `{{${name}}}`,
        );
      }
    }

    if (debugI18n) {
      const shortKey = key.length > 40 ? key.slice(0, 37) + '…' : key;
      return `⟦${shortKey}⟧ ${resolved}`;
    }
    return resolved;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, debugI18n }}>
      {children}
    </LanguageContext.Provider>
  );
};

// API helper
export { api };
